import { maskInsightRowForClient } from './maskCandidatePii';

export const candidateColumns = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'location', label: 'Location' },
  { key: 'skills', label: 'Skills' }
];

export const jobColumns = [
  { key: 'id', label: 'Job ID' },
  { key: 'title', label: 'Title' },
  { key: 'clientName', label: 'Client' },
  { key: 'location', label: 'Location' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'numberOfPositions', label: 'Positions' },
  { key: 'joinedCount', label: 'Joined' },
  { key: 'balanceToRecruit', label: 'Balance to Recruit' },
  { key: 'recruiterName', label: 'Recruiter' }
];

export const applicationColumns = [
  { key: 'id', label: 'Application ID' },
  { key: 'candidateName', label: 'Candidate' },
  { key: 'jobTitle', label: 'Job' },
  { key: 'clientName', label: 'Client' },
  { key: 'stage', label: 'Stage' },
  { key: 'offerStatus', label: 'Offer Status' },
  { key: 'notSelectedReason', label: 'Not Selected Reason' },
  { key: 'rejectionReason', label: 'Rejection Reason' },
  { key: 'statusNotes', label: 'Notes' },
  {
    key: 'updatedAt',
    label: 'Updated',
    format: r => (r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—')
  }
];

export const clientColumns = [
  { key: 'id', label: 'Client ID' },
  { key: 'name', label: 'Name' },
  { key: 'contactName', label: 'Contact Name' },
  { key: 'contactEmail', label: 'Contact Email' },
  { key: 'contactPhone', label: 'Contact Phone' },
  { key: 'paymentTerms', label: 'Payment Terms' },
  { key: 'status', label: 'Status' }
];

export const recruiterColumns = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'status', label: 'Status' }
];

export const invoiceColumns = [
  { key: 'id', label: 'Invoice ID' },
  { key: 'invoiceNumber', label: 'Invoice Number' },
  { key: 'clientName', label: 'Client' },
  { key: 'status', label: 'Status' },
  { key: 'feeType', label: 'Fee Type' },
  { key: 'feeValue', label: 'Fee Value' },
  { key: 'currency', label: 'Currency' },
  {
    key: 'dueAt',
    label: 'Due Date',
    format: r => (r.dueAt ? new Date(r.dueAt).toLocaleDateString() : '—')
  }
];

export function mapCandidates(list) {
  return (list || []).map(c => ({
    id: c.id,
    name: c.displayName || c.personal?.name || c.contact?.name || '—',
    email: c.displayEmail || c.contact?.email || '—',
    phone: c.displayPhone || c.contact?.phone || '—',
    location: c.location || '—',
    skills: (c.skills || []).join(', ') || '—'
  }));
}

export function mapJobs(list) {
  return (list || []).map(j => ({
    id: j.id,
    title: j.title || '—',
    clientName: j.clientName || j.clientId || '—',
    location: j.location || '—',
    status: j.status || '—',
    priority: j.priority || '—',
    numberOfPositions: j.numberOfPositions ?? 1,
    joinedCount: j.joinedCount ?? 0,
    balanceToRecruit: j.balanceToRecruit ?? Math.max(0, (j.numberOfPositions || 1) - (j.joinedCount || 0)),
    recruiterName: j.recruiterName || '—'
  }));
}

export function mapApplications(list, { maskForClient = false } = {}) {
  return (list || []).map(a => {
    const row = {
      id: a.id,
      candidateName: a.candidateName || a.candidateId || '—',
      jobTitle: a.jobTitle || a.jobId || '—',
      clientName: a.clientName || a.clientId || '—',
      stage: a.stage || '—',
      offerStatus: a.offerStatusLabel || a.offerStatus || '—',
      notSelectedReason: a.notSelectedReasonLabel || a.notSelectedReasonCode || '—',
      rejectionReason: a.rejectionReasonLabel || a.rejectionReasonCode || '—',
      statusNotes: a.statusNotes || a.displayStatusNotes || '—',
      updatedAt: a.updatedAt || a.createdAt
    };
    return maskForClient ? maskInsightRowForClient(row) : row;
  });
}

export function mapClients(list) {
  return (list || []).map(c => {
    const contact = c.contacts?.[0];
    return {
      id: c.id,
      name: c.name || '—',
      contactName: contact?.name || '—',
      contactEmail: contact?.email || '—',
      contactPhone: contact?.phone || '—',
      paymentTerms: c.paymentTerms || '—',
      status: c.status || 'active'
    };
  });
}

export function mapRecruiters(list) {
  return (list || []).map(r => ({
    id: r.id,
    name: r.name || '—',
    email: r.email || '—',
    phone: r.phone || '—',
    status: r.status || 'active'
  }));
}

export function mapInvoices(list, clients = []) {
  const byId = Object.fromEntries((clients || []).map(c => [c.id, c.name]));
  return (list || []).map(i => ({
    id: i.id,
    invoiceNumber: i.invoiceNumber || '—',
    clientName: byId[i.clientId] || i.clientId || '—',
    status: i.status || '—',
    feeType: i.feeType || '—',
    feeValue: i.feeValue ?? '—',
    currency: i.currency || '—',
    dueAt: i.dueAt
  }));
}

export const jobAnalyticsColumns = [
  { key: 'title', label: 'Job' },
  { key: 'clientName', label: 'Client' },
  { key: 'recruiterName', label: 'Recruiter' },
  { key: 'status', label: 'Status' },
  { key: 'positions', label: 'Positions' },
  { key: 'submissions', label: 'Submissions' },
  { key: 'interviewCount', label: 'Interview+' },
  { key: 'offerCount', label: 'Offer+' },
  { key: 'joinedCount', label: 'Joined' },
  { key: 'interviewToJoinedPct', label: 'Interview→Joined %' },
  { key: 'offerToJoinedPct', label: 'Offer→Joined %' },
  { key: 'fillPct', label: 'Fill %' },
  {
    key: 'completed',
    label: 'Completed',
    format: r => (r.completed ? 'Yes' : 'No')
  }
];

export const clientAnalyticsColumns = [
  { key: 'name', label: 'Client' },
  { key: 'jobCount', label: 'Jobs' },
  { key: 'activeJobs', label: 'Active Jobs' },
  { key: 'completedJobs', label: 'Completed Jobs' },
  { key: 'jobsCompletedPct', label: 'Jobs Completed %' },
  { key: 'submissions', label: 'Submissions' },
  { key: 'interviewCount', label: 'Interview+' },
  { key: 'offerCount', label: 'Offer+' },
  { key: 'joinedCount', label: 'Joined' },
  { key: 'interviewToJoinedPct', label: 'Interview→Joined %' },
  { key: 'offerToJoinedPct', label: 'Offer→Joined %' }
];

export const recruiterAnalyticsColumns = [
  { key: 'name', label: 'Recruiter' },
  { key: 'email', label: 'Email' },
  { key: 'assignedJobs', label: 'Jobs Assigned' },
  { key: 'completedJobs', label: 'Jobs Completed' },
  { key: 'jobsCompletedPct', label: 'Jobs Completed %' },
  { key: 'activeJobs', label: 'Active Jobs' },
  { key: 'submissions', label: 'Submissions' },
  { key: 'interviewCount', label: 'Interview+' },
  { key: 'offerCount', label: 'Offer+' },
  { key: 'joinedCount', label: 'Joined' },
  { key: 'interviewToJoinedPct', label: 'Interview→Joined %' },
  { key: 'offerToJoinedPct', label: 'Offer→Joined %' },
  { key: 'successRate', label: 'Overall Success %' }
];

export const candidateAnalyticsColumns = [
  { key: 'name', label: 'Candidate' },
  { key: 'email', label: 'Email' },
  { key: 'applicationCount', label: 'Applications' },
  { key: 'bestStage', label: 'Best Stage' },
  { key: 'activeStages', label: 'Active Stages' },
  { key: 'interviewCount', label: 'Interview+' },
  { key: 'offerCount', label: 'Offer+' },
  { key: 'joinedCount', label: 'Joined' },
  { key: 'interviewToJoinedPct', label: 'Interview→Joined %' }
];

export const candidateDemographicColumns = [
  { key: 'name', label: 'Candidate' },
  { key: 'email', label: 'Email' },
  { key: 'location', label: 'Location' },
  { key: 'age', label: 'Age' },
  { key: 'ageBand', label: 'Age Band' },
  { key: 'experienceYears', label: 'Experience (yrs)' },
  { key: 'experienceBand', label: 'Experience Band' },
  { key: 'education', label: 'Education' },
  { key: 'expertise', label: 'Area of Expertise' },
  { key: 'skills', label: 'Core Competencies' },
  { key: 'technicalSkills', label: 'Technical Skills' },
  { key: 'gender', label: 'Gender' }
];

export function mapJobAnalytics(rows) {
  return (rows || []).map(r => ({
    id: r.id,
    title: r.title,
    clientName: r.clientName,
    recruiterName: r.recruiterName,
    status: r.status,
    positions: r.positions,
    submissions: r.submissions,
    interviewCount: r.interviewCount,
    offerCount: r.offerCount,
    joinedCount: r.joinedCount,
    interviewToJoinedPct: r.interviewToJoinedPct ? `${r.interviewToJoinedPct}%` : '0%',
    offerToJoinedPct: r.offerToJoinedPct ? `${r.offerToJoinedPct}%` : '0%',
    fillPct: r.fillPct ? `${r.fillPct}%` : '0%',
    completed: r.completed
  }));
}

export function mapClientAnalytics(rows) {
  return (rows || []).map(r => ({
    id: r.id,
    name: r.name,
    jobCount: r.jobCount,
    activeJobs: r.activeJobs,
    completedJobs: r.completedJobs,
    jobsCompletedPct: r.jobsCompletedPct ? `${r.jobsCompletedPct}%` : '0%',
    submissions: r.submissions,
    interviewCount: r.interviewCount,
    offerCount: r.offerCount,
    joinedCount: r.joinedCount,
    interviewToJoinedPct: r.interviewToJoinedPct ? `${r.interviewToJoinedPct}%` : '0%',
    offerToJoinedPct: r.offerToJoinedPct ? `${r.offerToJoinedPct}%` : '0%'
  }));
}

export function mapRecruiterAnalytics(rows) {
  return (rows || []).map(r => ({
    id: r.id,
    name: r.name,
    email: r.email,
    assignedJobs: r.assignedJobs,
    completedJobs: r.completedJobs,
    jobsCompletedPct: r.jobsCompletedPct ? `${r.jobsCompletedPct}%` : '0%',
    activeJobs: r.activeJobs,
    submissions: r.submissions,
    interviewCount: r.interviewCount,
    offerCount: r.offerCount,
    joinedCount: r.joinedCount,
    interviewToJoinedPct: r.interviewToJoinedPct ? `${r.interviewToJoinedPct}%` : '0%',
    offerToJoinedPct: r.offerToJoinedPct ? `${r.offerToJoinedPct}%` : '0%',
    successRate: r.successRate ? `${r.successRate}%` : '0%'
  }));
}

export function mapCandidateAnalytics(rows, { maskForClient = false } = {}) {
  return (rows || []).map(r => {
    const row = {
      id: r.id,
      name: r.name,
      email: r.email,
      applicationCount: r.applicationCount,
      bestStage: r.bestStage,
      activeStages: r.activeStages,
      interviewCount: r.interviewCount,
      offerCount: r.offerCount,
      joinedCount: r.joinedCount,
      interviewToJoinedPct: r.interviewToJoinedPct ? `${r.interviewToJoinedPct}%` : '0%',
      _candidateId: r.id
    };
    return maskForClient ? maskInsightRowForClient(row) : row;
  });
}

export function mapCandidateDemographics(rows, { maskForClient = false } = {}) {
  return (rows || []).map(r => {
    const row = {
      id: r.id,
      name: r.name,
      email: r.email,
      location: r.location,
      age: r.age ?? '—',
      ageBand: r.ageBand,
      experienceYears: r.experienceYears,
      experienceBand: r.experienceBand,
      education: r.education,
      expertise: r.expertise,
      skills: r.skills,
      technicalSkills: r.technicalSkills,
      gender: r.gender,
      _candidateId: r.id
    };
    return maskForClient ? maskInsightRowForClient(row) : row;
  });
}
